import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Button, Box, Typography } from '@mui/material';
import protocolService from '../../services/protocolService';

interface ProtocolEmailDialogProps {
  open: boolean;
  onClose: () => void;
  protocolId: string | number;
  defaultTo: string;
  defaultSubject: string;
  defaultBody?: string;
  attachmentName?: string;
  attachmentUrl?: string;
  onSent?: () => void;
  onDraft?: () => void;
}

export default function ProtocolEmailDialog({ open, onClose, protocolId, defaultTo, defaultSubject, defaultBody, attachmentName, attachmentUrl, onSent, onDraft }: ProtocolEmailDialogProps) {
  const [to, setTo] = useState(defaultTo || '');
  const [subject, setSubject] = useState(defaultSubject || '');
  const [body, setBody] = useState(defaultBody || '');
  const [loading, setLoading] = useState(false);
  const [wysiwyg, setWysiwyg] = useState(true);
  const editableRef = useRef<HTMLDivElement | null>(null);
  const userEditingRef = useRef(false);
  const [toError, setToError] = useState<string | null>(null);

  const splitTokens = (value: string) => (value || '')
    .replace(/;/g, ',')
    .split(/[\s,\n]+/)
    .map(s => s.trim())
    .filter(Boolean);

  const extractEmail = (token: string) => {
    if (!token) return '';
    const m = token.match(/<([^>]+)>/);
    let addr = m ? m[1] : token;
    addr = addr.replace(/^mailto:/i, '');
    return addr.trim();
  };

  const isValidEmail = (email: string) => /^(?=.{3,254}$)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);

  const normalizeTo = (value: string) => splitTokens(value).map(extractEmail);

  const validateTo = (value: string) => {
    const list = normalizeTo(value);
    const invalid = list.filter(t => !isValidEmail(t));
    if (!list.length) return { error: 'Podaj co najmniej jeden adres e‑mail', normalized: list };
    if (invalid.length) return { error: `Nieprawidłowe adresy: ${invalid.join(', ')}`, normalized: list };
    return { error: null, normalized: list };
  };

  useEffect(() => {
    if (!open) return;
    // Reset z wartości domyślnych przy każdym otwarciu
    setTo(defaultTo || '');
    setSubject(defaultSubject || '');
    // Jeśli nie ma body, zbuduj prostą treść startową
    const base = defaultBody || 'Szanowni Państwo,\n\nw załączeniu protokół odbioru robót,';
    setBody(base.replace(/\n/g, '<br>'));
    // Walidacja "Do" po otwarciu
    const v = validateTo(defaultTo || '');
    setToError(v.error);
  }, [open, defaultTo, defaultSubject, defaultBody]);

  useEffect(() => {
    if (wysiwyg && editableRef.current && !userEditingRef.current) {
      editableRef.current.innerHTML = body || '';
    }
  }, [wysiwyg, open, body]);

  const handleSend = async () => {
    const { error, normalized } = validateTo(to);
    if (error) { setToError(error); return; }
    const toHeader = normalized.join(', ');
    setLoading(true);
    try {
      const html = wysiwyg ? (editableRef.current?.innerHTML || '') : body;
      await protocolService.sendEmail(String(protocolId), {
        to: toHeader,
        subject,
        body: html
      });
      onSent && onSent();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    const { error, normalized } = validateTo(to);
    if (error) { setToError(error); return; }
    const toHeader = normalized.join(', ');
    setLoading(true);
    try {
      const html = wysiwyg ? (editableRef.current?.innerHTML || '') : body;
      await protocolService.createDraft(String(protocolId), { to: toHeader, subject, body: html });
      onDraft && onDraft();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Wiadomość e‑mail</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField 
              label="Do" 
              type="text" 
              fullWidth 
              size="small" 
              value={to} 
              onChange={(e) => { setTo(e.target.value); const v = validateTo(e.target.value); setToError(v.error); }}
              error={Boolean(toError)}
              helperText={toError || 'Możesz podać wiele adresów oddzielonych przecinkami/średnikami lub w formacie Nazwa <email@domena>'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Temat" fullWidth size="small" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Grid>
          {attachmentName && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Załączniki:</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="small" variant="outlined" component={attachmentUrl ? 'a' as any : 'button'} href={attachmentUrl || undefined} target="_blank">
                  {attachmentName}
                </Button>
                <Typography variant="caption" color="text.secondary">PDF zostanie dołączony automatycznie przy wysyłce.</Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button size="small" onClick={() => setWysiwyg(!wysiwyg)}>
                {wysiwyg ? 'Pokaż HTML' : 'Tryb edycji (WYSIWYG)'}
              </Button>
            </Box>
            {wysiwyg ? (
              <Box
                sx={{
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  p: 1.5,
                  minHeight: 200,
                  backgroundColor: '#fff',
                  '&:focus': { outline: 'none', borderColor: 'rgba(25,118,210,0.5)', boxShadow: '0 0 0 3px rgba(25,118,210,0.1)' }
                }}
                contentEditable
                suppressContentEditableWarning
                ref={editableRef}
                onInput={(e) => {
                  userEditingRef.current = true;
                  setBody((e.target as HTMLElement).innerHTML);
                  requestAnimationFrame(() => { userEditingRef.current = false; });
                }}
              />
            ) : (
              <TextField label="Treść (HTML)" fullWidth size="small" multiline minRows={8} value={body} onChange={(e) => setBody(e.target.value)} />
            )}
            <Typography variant="caption" color="text.secondary">Podpis z profilu użytkownika zostanie dołączony automatycznie.</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Zamknij</Button>
        <Button onClick={handleCreateDraft} disabled={loading || Boolean(toError)} variant="outlined">Utwórz draft</Button>
        <Button onClick={handleSend} disabled={loading || Boolean(toError)} variant="contained">Wyślij</Button>
      </DialogActions>
    </Dialog>
  );
}


